<template>
  <div class="mx-auto w-full fadeIn segment-editor-container">
    <h2 class="text-center">
      {{ editMode ? `Edit - ${segment.name}` : "Add Segment" }}
    </h2>
    <div class="edyst-form-group mt-4">
      <label for="segment name" class="pb-0">Segment Name</label>
      <input
        id="segment name"
        ref="segmentName"
        v-model.trim="segment.name"
        v-validate="'required'"
        type="text"
        class="template-name"
        name="template name"
        value="Untitled Segment"
      />
      <small class="err-msg text-red block mt-2">
        {{ veeValidateErrors.first("template name") }}
      </small>
    </div>

    <div class="edyst-form-group flex justify-start items-center my-8">
      Contacts Match
      <select
        id="any-all"
        v-model="segment.match"
        style="width: 5rem; margin-top: 0;"
        class="mx-2"
        @change="fetchMatchCount"
      >
        <option :value="1">Any</option>
        <option :value="2">All</option>
      </select>
      of the following conditions
    </div>

    <p>
      Unique Contacts:
      <strong v-if="!loadingCount">{{ contactsMatchCount }}</strong>
      <strong v-else class="blink tracking-wide">•••</strong>
    </p>

    <div class="edyst-form-group mt-8">
      <h3>Conditions</h3>
      <div class="flex items-start mt-2 ml-4">
        <label for="cohort names" class="condition-label">Cohort Names</label>
        <MultiSelect
          ref="courseSelector"
          v-model="segment.conditions[0].value"
          :options="cohortOptions"
          placeholder="Select Cohorts"
          :closeOnSelect="false"
          :multiple="true"
          :clearOnSelect="false"
          :preserveSearch="true"
          @select="fetchMatchCount"
          @remove="fetchMatchCount"
        />
      </div>
      <div class="flex items-start mt-2 ml-4">
        <label class="condition-label" for="emails">Emails</label>
        <div class="w-full">
          <textarea
            id="emails"
            v-model.trim="segment.conditions[1].value"
            name="emails"
            class="emails"
            placeholder="Paste emails here"
            @change="fetchMatchCount"
          ></textarea>
          <button
            class="text-sm text-grey-dark mr-2 rounded-sm bg-grey-lighter px-1 font-normal border border-grey-light"
            @click="formatEmails(true)"
          >
            Sanitize Emails
          </button>
          <small class="text-grey-dark text-sm"
            >Emails Count:
            {{ getEmailsCount(segment.conditions[1].value) }}</small
          >
        </div>
      </div>
      <div class="text-right mt-8">
        <button
          v-if="!editMode"
          class="px-4 py-2 rounded-sm mr-4 text-grey-darker hover:bg-grey-lighter hover:text-grey-darkest focus:text-grey-darkest"
          @click="$emit('discard-change')"
        >
          Cancel
        </button>
        <button
          class="bg-green text-white px-4 py-2 rounded-sm"
          @click="handleSubmit"
        >
          {{ editMode ? "Update" : "Save" }} Segment
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MultiSelect from "vue-multiselect"
import siteConfig from "@/utils/siteConfig"
import {
  updateSegment,
  getSegment,
  createSegment,
  getSegmentMatchCount,
} from "@/apis/notification/index"

export default {
  components: {
    MultiSelect,
  },
  data() {
    return {
      timeoutId: null,
      contactsMatchCount: 0,
      loadingCount: false,
      notyConfig: {
        layout: "bottomLeft",
        animation: {
          open: "slideUp",
          close: "fadeOut",
        },
      },
      segment: {
        name: "",
        match: 1,
        conditions: [
          { property: 1, operator: 1, value: [] },
          { property: 2, operator: 1, value: "" },
        ],
      },
      cohortOptions: [],
      selectedCohorts: [],
    }
  },
  computed: {
    editMode() {
      return this.$route.params.segment_id
    },
  },
  created() {
    this.getCohorts()
    if (this.editMode) {
      this.getSegmentDetails()
    }
  },
  mounted() {
    this.$refs.segmentName.focus()
  },
  methods: {
    getEmailsCount(string) {
      if (string) {
        const emails = string.replace(/\n/g, ",").split(",")
        return emails.length
      }
      return 0
    },
    async handleSubmit() {
      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again", this.notyConfig)
        return
      }
      if (this.editMode) {
        this.updateSegment()
      } else {
        this.addSegment()
      }
    },
    getSegmentDetails() {
      const loader = this.$loading.show({ loader: "bars" })
      getSegment(this.$route.params.segment_id)
        .then(res => {
          this.segment.name = res.data.name
          window.pageTitle = `Edit - ${res.data.name}`
          this.segment.match = res.data.match
          if (res.data.conditions.length > 0) {
            // populate cohort condition
            const cohortCondition = res.data.conditions.find(
              item => item.property === 1
            )
            if (cohortCondition) {
              this.segment.conditions[0].value = cohortCondition.value
            }
            // populate email condition
            const emailCondition = res.data.conditions.find(
              item => item.property === 2
            )
            if (emailCondition) {
              this.segment.conditions[1].value = emailCondition.value
            }
          }
          this.requestSegmentMatchCount()
        })
        .catch(err => {
          console.error(err)
          this.$noty.error("Failed to fetch segment details", this.notyConfig)
        })
        .finally(() => {
          loader.hide()
        })
    },
    requestSegmentMatchCount() {
      this.loadingCount = true
      getSegmentMatchCount(this.segment)
        .then(res => {
          this.contactsMatchCount = res.data.contacts_count
        })
        .catch(err => {
          console.error(err)
          this.$noty.error(
            "Failed to get contacts match count",
            this.notyConfig
          )
        })
        .finally(() => {
          this.loadingCount = false
        })
    },
    fetchMatchCount() {
      this.loadingCount = true
      clearTimeout(this.timeoutId)
      this.timeoutId = setTimeout(() => {
        this.requestSegmentMatchCount()
      }, 2000)
    },
    addSegment() {
      this.formatEmails()
      createSegment(this.segment)
        .then(() => {
          this.$emit("segment-created")
        })
        .catch(err => {
          this.$noty.error("Failed to add segment", this.notyConfig)
          console.error("Failed to add segment", err)
        })
    },
    updateSegment() {
      this.formatEmails()
      updateSegment(this.$route.params.segment_id, this.segment)
        .then(() => {
          this.$noty.info("Segment updated", this.notyConfig)
          this.$router.push({
            name: "EdystNotification",
            query: { tab: "segments" },
          })
        })
        .catch(err => {
          console.error(err)
          this.$noty.error("Failed to update segment details", this.notyConfig)
        })
        .catch(err => console.error("Not able to validate input fields", err))
    },
    getCohorts() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}cohorts/?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          this.cohortOptions = res.data.items.map(item => item.name)
        })
        .catch(err => {
          console.error("Failed to fetch the cohorts", err)
        })
    },
    formatEmails(nofityUser = false) {
      const emailProp = this.segment.conditions.find(
        item => item.property === 2
      )
      let values = emailProp.value
      // replace all new lines with comma
      values = values.replace(/\n/g, ",")
      values = values.split(",")
      // remove duplicates
      values = [...new Set(values)]
      // remove empty values
      values = values.filter(e => e.length > 1)
      // separate emails by new lines
      values = values.join("\n")
      emailProp.value = values
      if (nofityUser) {
        this.$noty.info("Emails formatted and duplicates are removed")
      }
    },
  },
}
</script>

<style scoped>
.segment-editor-container {
  overflow: auto;
  max-width: 1200px;
  height: 100%;
  margin: auto;
  padding: 2rem 4rem;
}

.condition-label {
  flex-shrink: 0;
  width: 8rem;
  margin-top: 0.5rem;
}

.emails {
  height: 10rem;
}

.blink {
  animation: blink 2s linear infinite;
}

@keyframes blink {
  0% {
    opacity: 0.2;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.2;
  }
}
</style>
